import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PageHeader } from "@/components/layout/PageHeader";
import { Timeline } from "@/components/timeline/Timeline";
import { toast } from "sonner";
import { formatDateLong, formatTimeRange } from "@/lib/format/date";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  Ruler,
  AlertCircle,
  Eye,
  Phone,
} from "lucide-react";

interface Appointment {
  id: string;
  status: string;
  otp?: string;
  slot: {
    startAt: string;
    endAt: string;
    slotType: string;
  };
  area: {
    name: string;
  };
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    pincode: string;
  };
  customer: {
    publicId: string;
    name: string;
    phone?: string;
  };
  assignedExec?: {
    publicId: string;
    name: string;
    phone?: string;
  } | null;
  measurementVersions?: {
    id: string;
    versionNo: number;
    status: string;
    capturedAt: string;
  }[];
}

interface TimelineEntry {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  actor?: { id: string; publicId: string; name: string; role: string } | null;
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

const statusConfig: Record<string, { color: string; label: string }> = {
  DRAFT: { color: "bg-gray-100 text-gray-700", label: "Draft" },
  CONFIRMED: { color: "bg-blue-100 text-blue-700", label: "Confirmed" },
  ASSIGNED: { color: "bg-cyan-100 text-cyan-700", label: "Assigned" },
  EN_ROUTE: { color: "bg-orange-100 text-orange-700", label: "En Route" },
  ARRIVED: { color: "bg-teal-100 text-teal-700", label: "Arrived" },
  IN_PROGRESS: { color: "bg-indigo-100 text-indigo-700", label: "In Progress" },
  MEASUREMENTS_SUBMITTED: { color: "bg-blue-100 text-blue-700", label: "Submitted" },
  PENDING_CUSTOMER_ACCEPTANCE: { color: "bg-amber-100 text-amber-700", label: "Pending Acceptance" },
  ACCEPTED: { color: "bg-emerald-100 text-emerald-700", label: "Accepted" },
  CORRECTION_REQUESTED: { color: "bg-orange-100 text-orange-700", label: "Correction Requested" },
  CANCELLED: { color: "bg-red-100 text-red-700", label: "Cancelled" },
  CLOSED: { color: "bg-slate-100 text-slate-600", label: "Closed" },
};

export default function AppointmentDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const appointmentId = params.id as string;

  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);

  useEffect(() => {
    fetchData();
  }, [appointmentId]);

  async function fetchData() {
    try {
      const [appointmentRes, timelineRes] = await Promise.all([
        fetch(`/api/customer/appointments/${appointmentId}`),
        fetch(`/api/timeline/appointment?appointmentId=${appointmentId}`),
      ]);

      if (!appointmentRes.ok) throw new Error("Failed to fetch appointment");

      const appointmentData = await appointmentRes.json();
      setAppointment(appointmentData.appointment);

      if (timelineRes.ok) {
        const timelineData = await timelineRes.json();
        setTimeline(timelineData.timeline || []);
      }
    } catch (error) {
      toast.error("Failed to load appointment");
      navigate("/customer/appointments");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Appointment Details" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-[300px] lg:col-span-2" />
          <Skeleton className="h-[300px]" />
        </div>
      </div>
    );
  }

  if (!appointment) return null;

  const status = statusConfig[appointment.status] || statusConfig.DRAFT;
  const pendingVersion = appointment.measurementVersions?.find(
    (v) => v.status === "PENDING_ACCEPTANCE"
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Appointment Details"
        actions={
          <Button variant="outline" onClick={() => navigate("/customer/appointments")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Appointments
          </Button>
        }
      />

      {appointment.status === "PENDING_CUSTOMER_ACCEPTANCE" && pendingVersion && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="py-5">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">
                    Measurements Ready for Review
                  </h3>
                  <p className="text-sm text-slate-600">
                    Your measurements have been submitted. Please review and accept them.
                  </p>
                </div>
              </div>
              <Button
                onClick={() =>
                  navigate(`/customer/measurements/review/${pendingVersion.id}`)
                }
              >
                <Eye className="h-4 w-4 mr-2" />
                Review Measurements
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Ruler className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Measurement Visit</CardTitle>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {appointment.area.name}
                    </p>
                  </div>
                </div>
                <Badge className={status.color}>{status.label}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Date</p>
                    <p className="font-medium">
                      {formatDateLong(appointment.slot.startAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Time</p>
                    <p className="font-medium">
                      {formatTimeRange(appointment.slot.startAt, appointment.slot.endAt)}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{appointment.address.line1}</p>
                  {appointment.address.line2 && (
                    <p className="text-sm">{appointment.address.line2}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {appointment.address.city}, {appointment.address.state} -{" "}
                    {appointment.address.pincode}
                  </p>
                </div>
              </div>

              {appointment.assignedExec && (
                <>
                  <Separator />
                  <div className="flex items-start gap-3">
                    <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Assigned Executive</p>
                      <p className="font-medium">{appointment.assignedExec.name}</p>
                      {appointment.assignedExec.phone && (
                        <div className="flex items-center gap-2 mt-1">
                          <Phone className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {appointment.assignedExec.phone}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {appointment.otp && ["EN_ROUTE", "ARRIVED", "IN_PROGRESS"].includes(appointment.status) && (
                <>
                  <Separator />
                  <div className="p-4 bg-primary/5 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground mb-1">Verification OTP</p>
                    <p className="text-3xl font-bold tracking-widest text-primary">
                      {appointment.otp}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Share this code with the executive to verify the visit
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {appointment.measurementVersions && appointment.measurementVersions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Measurement Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {appointment.measurementVersions.map((version) => (
                    <div
                      key={version.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-sm">Version {version.versionNo}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(version.capturedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={
                            version.status === "ACCEPTED_ACTIVE"
                              ? "border-emerald-300 text-emerald-700"
                              : version.status === "PENDING_ACCEPTANCE"
                              ? "border-amber-300 text-amber-700"
                              : ""
                          }
                        >
                          {version.status.replace(/_/g, " ")}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate(`/customer/measurements/review/${version.id}`)
                          }
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline entries={timeline} />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
